import { useTranslation } from 'next-i18next';
import { WalletCards } from 'lucide-react';
import { ApiHeaderInfoOk } from '@type/v1-api-types';
import { Anchor, Tooltip } from '@components/common';
import { NeuroIcon } from '@components/common/Icons';
import { getI18nLinkHref } from '@services/i18nPaths';
import { dataTestId } from '@services/data-test-id';

interface WalletSectionProps {
  className?: string;
  locale?: Locale;
  headerInfo?: ApiHeaderInfoOk;
}

/**
 * @description Dedicated wallet icon link in the navbar (mirrors the messages icon). Links straight
 * to the wallet Services tab. Rendered on both desktop and mobile (on mobile it sits before the
 * account avatar). On hover it shows the same tooltip as the account icon (balance, pending top-up,
 * discount), titled with the link name.
 */
export const WalletSection = ({ className, locale = 'ro', headerInfo }: WalletSectionProps) => {
  const { t } = useTranslation('common');

  const info = headerInfo as (ApiHeaderInfoOk & {
    ordersNotificationData?: { totalCredits?: number };
    discountPercentage?: number;
  }) | undefined;
  const walletCredits = info?.walletCredits ?? 0;
  const totalCredits = info?.ordersNotificationData?.totalCredits ?? 0;
  const discount = info?.discountPercentage ?? 0;

  return (
    <Tooltip
      position="bottom"
      floating
      useFloatingPortal
      displayStyle="block"
      className={`relative block ${className ?? ''}`}
      tooltipText={(
        <div className="flex flex-col gap-1.5 p-2 text-left text-xs">
          <div className="flex items-start justify-between gap-4">
            <span className="text-ink-medium">{t('navbar.credits.tooltip-balance')}</span>
            <span className="font-bold">{walletCredits}</span>
          </div>
          {totalCredits > 0 && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-ink-medium">{t('navbar.topup_initiated')}</span>
              <span className="font-bold">{totalCredits}</span>
            </div>
          )}
          {discount > 0 && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-ink-medium">{t('consultant.info.box.basic.title')}</span>
              <span className="font-bold text-secondary">
                {discount}
                %
              </span>
            </div>
          )}
        </div>
      )}
    >
      <Anchor
        href={getI18nLinkHref('/wallet/transactions', locale)}
        styling="none"
        className="relative flex cursor-pointer items-center gap-2.5 px-1 py-2.5 outline-none hover:text-primary lg:rounded-xl lg:border lg:border-border lg:px-3.5 lg:py-1.5 lg:hover:border-primary-light"
        aria-label={t('header.label.wallet')}
        {...dataTestId('navbar.walletButton')}
      >
        <WalletCards className="size-5 shrink-0 text-ink" />
        {/* Desktop balance/discount (mobile keeps the icon only). The "Wallet" label +
            divider only appear from xl up — on lg they'd widen the row enough to wrap the
            navbar on pages that also show the CV search. */}
        <span className="hidden items-center gap-2.5 lg:flex">
          <span className="hidden text-sm font-bold text-ink xl:inline">{t('header.label.wallet')}</span>
          <span className="hidden h-5.5 w-px bg-border xl:block" />
          <span className="flex flex-col leading-tight">
            <span className="flex items-center gap-0.5 text-[13px] font-bold text-ink">
              {walletCredits}
              <NeuroIcon className="size-1.75" />
            </span>
            {discount > 0 && (
              <span className="text-xs font-bold text-secondary">
                {discount}
                % OFF
              </span>
            )}
          </span>
        </span>
      </Anchor>
    </Tooltip>
  );
};
